import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { useBotWallet } from './useBotWallet';
import { useEffectiveUser } from './ImpersonationContext';

const DEFAULT_SNIPE_CFG = {
  min_liquidity_usd: 3000, min_buyers_5min: 3, min_volume_5min_usd: 1000,
  delay_sec: 120, amount_sol: 0.1,
  stop_loss_pct: 30, take_profit_pct: 100, trailing_stop_pct: 20, max_positions: 3,
};
const DEFAULT_MOMENTUM_CFG = {
  min_volume_sol_10min: 10, min_unique_traders: 10, max_price_up_pct: 50,
  min_liquidity_usd: 10000, min_age_h: 1, max_age_h: 24, amount_sol: 0.2,
  stop_loss_pct: 25, take_profit_pct: 80, trailing_stop_pct: 20, max_positions: 3,
};

function fmtRoi(n) { const v = parseFloat(n || 0); return (v >= 0 ? '+' : '') + v.toFixed(1) + '%'; }
function timeAgo(ts) {
  if (!ts) return '?';
  const d = Date.now() / 1000 - ts;
  if (d < 60) return Math.floor(d) + 's';
  if (d < 3600) return Math.floor(d / 60) + 'min';
  return Math.floor(d / 3600) + 'h';
}

const S = {
  page:   { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)', overflow: 'hidden', background: '#060809' },
  scroll: { flex: 1, overflowY: 'auto', padding: '14px 18px' },
  card:   { background: '#0b0e14', border: '1px solid #1a2030', borderRadius: 10, marginBottom: 14, overflow: 'hidden' },
  head:   { padding: '12px 16px', borderBottom: '1px solid #1a2030', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  lbl:    { fontSize: 9, fontWeight: 700, color: '#3a4a5a', letterSpacing: '0.8px', marginBottom: 4 },
  inp:    { background: '#060809', border: '1px solid #1a2030', borderRadius: 5, color: '#c0c8d8', fontSize: 12, padding: '6px 10px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  grid3:  { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, padding: '10px 16px' },
  divider:{ fontSize: 9, fontWeight: 700, color: '#3a4a5a', letterSpacing: '0.8px', padding: '8px 16px 0' },
};

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
      background: value ? 'linear-gradient(135deg,#00e5cc,#00b8a0)' : '#1a2030', transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: value ? 22 : 3, width: 18, height: 18,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
      }} />
    </div>
  );
}

function StatRow({ items }) {
  return (
    <div style={{ display: 'flex', gap: 6, padding: '8px 16px', borderTop: '1px solid #1a2030' }}>
      {items.map(([l, v, color]) => (
        <div key={l} style={{ flex: 1, background: '#060809', border: '1px solid #1a2030', borderRadius: 6, padding: '7px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: '#3a4a5a', letterSpacing: '0.5px', marginBottom: 4 }}>{l}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: color || '#c0c8d8' }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function CfgInput({ label, value, onChange, step, min, max }) {
  return (
    <div>
      <div style={S.lbl}>{label}</div>
      <input type="number" value={value} onChange={e => onChange(Number(e.target.value))}
        style={S.inp} step={step || 1} min={min} max={max} />
    </div>
  );
}

function HistoryRow({ t }) {
  const roi = parseFloat(t.roi || 0);
  const open = t.resultat === 'En cours';
  const color = open ? '#f0a500' : roi >= 0 ? '#00e5cc' : '#ff6b6b';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 16px', borderBottom: '1px solid #0d1117', fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 700, color: '#c0c8d8' }}>{t.symbol || t.mint?.slice(0, 8)}</span>
        <span style={{ color: '#3a4a5a', fontSize: 10 }}>{timeAgo(t.entree_ts)} ago</span>
        {t.close_reason && (
          <span style={{ fontSize: 9, fontWeight: 700, color: '#5a6a80', background: '#0d1117', padding: '1px 5px', borderRadius: 3 }}>
            {t.close_reason}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#5a6a80' }}>{(t.achat_usd || 0).toFixed(2)}$</span>
        <span style={{ color, fontWeight: 700, minWidth: 52, textAlign: 'right' }}>
          {open ? 'En cours' : fmtRoi(roi)}
        </span>
      </div>
    </div>
  );
}

export default function SnipersTab() {
  const { effectiveUserId } = useEffectiveUser();
  const { walletList } = useBotWallet();
  const realWallets = walletList.filter(w => !w.isSim);

  const [loaded,          setLoaded]          = useState(false);
  const [snipeEnabled,    setSnipeEnabled]     = useState(false);
  const [snipeConfig,     setSnipeConfig]      = useState(DEFAULT_SNIPE_CFG);
  const [momentumEnabled, setMomentumEnabled]  = useState(false);
  const [momentumConfig,  setMomentumConfig]   = useState(DEFAULT_MOMENTUM_CFG);
  const [botWalletId,     setBotWalletId]      = useState(null);
  const [snipeH,      setSnipeH]      = useState([]);
  const [momH,        setMomH]        = useState([]);
  const [snipeSaving, setSnipeSaving] = useState(false);
  const [snipeSaved,  setSnipeSaved]  = useState(false);
  const [momSaving,   setMomSaving]   = useState(false);
  const [momSaved,    setMomSaved]    = useState(false);

  // ── Chargement initial ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const userId = effectiveUserId || session.user.id;

      const { data, error: loadErr } = await supabase.from('sniper_settings').select('*').eq('user_id', userId).single();
      console.log('[SNIPERS] loadSettings →', { data, error: loadErr?.message });
      if (data) {
        setSnipeEnabled(!!data.snipe_enabled);
        setMomentumEnabled(!!data.momentum_enabled);
        setSnipeConfig({ ...DEFAULT_SNIPE_CFG,    ...(data.snipe_config    || {}) });
        setMomentumConfig({ ...DEFAULT_MOMENTUM_CFG, ...(data.momentum_config || {}) });
        setBotWalletId(data.bot_wallet_id || null);
      } else {
        await supabase.from('sniper_settings').insert({
          user_id: userId, snipe_enabled: false, momentum_enabled: false,
          snipe_config: DEFAULT_SNIPE_CFG, momentum_config: DEFAULT_MOMENTUM_CFG,
          bot_wallet_id: null,
        });
        // states déjà initialisés aux defaults via useState
      }
      setLoaded(true);

      const [{ data: sh }, { data: mh }] = await Promise.all([
        supabase.from('trades').select('*').eq('user_id', userId).eq('source', 'snipe')
          .order('entree_ts', { ascending: false }).limit(10),
        supabase.from('trades').select('*').eq('user_id', userId).eq('source', 'momentum')
          .order('entree_ts', { ascending: false }).limit(10),
      ]);
      setSnipeH(sh || []);
      setMomH(mh || []);
    })();
  }, [effectiveUserId]);

  // ── Sauvegarde explicite vers Supabase ──────────────────────────
  const saveToSupabase = useCallback(async (setSaving, setSaved) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const userId = effectiveUserId || session.user.id;
      const { error: saveErr } = await supabase.from('sniper_settings').upsert({
        user_id: userId,
        snipe_enabled:    snipeEnabled,
        momentum_enabled: momentumEnabled,
        snipe_config:     snipeConfig,
        momentum_config:  momentumConfig,
        bot_wallet_id:    botWalletId || null,
        updated_at:       new Date().toISOString(),
      }, { onConflict: 'user_id' });
      if (saveErr) throw new Error(saveErr.message);
      console.log('[SNIPERS] upsert OK');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { console.error('[SNIPERS] save:', e.message); }
    finally { setSaving(false); }
  }, [snipeEnabled, momentumEnabled, snipeConfig, momentumConfig, botWalletId, effectiveUserId]);

  const sc = (key, val) => setSnipeConfig(prev    => ({ ...prev, [key]: val }));
  const mc = (key, val) => setMomentumConfig(prev => ({ ...prev, [key]: val }));

  if (!loaded) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 52px)', color: '#3a4a5a', fontSize: 12 }}>
      Chargement...
    </div>
  );

  const C = snipeConfig;
  const M = momentumConfig;

  // Stats snipe
  const snipeClosed = snipeH.filter(t => t.resultat !== 'En cours');
  const snipeWin    = snipeClosed.filter(t => t.resultat === 'Gagnant').length;
  const snipeWr     = snipeClosed.length ? Math.round(snipeWin / snipeClosed.length * 100) + '%' : '—';

  // Stats momentum
  const momClosed = momH.filter(t => t.resultat !== 'En cours');
  const momWin    = momClosed.filter(t => t.resultat === 'Gagnant').length;
  const momWr     = momClosed.length ? Math.round(momWin / momClosed.length * 100) + '%' : '—';

  return (
    <div style={S.page}>

      {/* ── Top bar ── */}
      <div style={{ padding: '10px 18px', borderBottom: '1px solid #1a2030', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#c0c8d8', letterSpacing: '0.5px' }}>SNIPERS AUTO</div>
          <div style={{ fontSize: 10, color: '#3a4a5a', marginTop: 1 }}>Pump.fun WS · Raydium onLogs 10min window</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={S.lbl}>WALLET BOT</div>
            <select value={botWalletId || ''} onChange={e => setBotWalletId(e.target.value || null)}
              style={{ ...S.inp, width: 170, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>
              <option value="">— Sélectionner —</option>
              {realWallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={S.scroll}>

        {/* ═══════════════ Section 1 : Sniping ═══════════════ */}
        <div style={S.card}>
          <div style={S.head}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#c0c8d8' }}>🎯 Sniping nouveaux tokens</div>
              <div style={{ fontSize: 10, color: '#3a4a5a', marginTop: 2 }}>Pump.fun WebSocket · Achat automatique après délai de sécurité</div>
            </div>
            <Toggle value={snipeEnabled} onChange={setSnipeEnabled} />
          </div>

          <StatRow items={[
            ['POSITIONS OUVERTES', snipeH.filter(t => t.resultat === 'En cours').length],
            ['SNIPES TOTAL',       snipeH.length],
            ['GAGNANTS',           snipeWin],
            ['WIN RATE',           snipeWr, snipeClosed.length ? (snipeWin / snipeClosed.length >= 0.5 ? '#00e5cc' : '#ff6b6b') : '#5a6a80'],
          ]} />

          {snipeEnabled && (<>
            <div style={S.divider}>FILTRES D'ENTRÉE</div>
            <div style={S.grid3}>
              <CfgInput label="DÉLAI (sec)"        value={C.delay_sec}          onChange={v => sc('delay_sec', v)}          min={0} />
              <CfgInput label="LIQUIDITÉ MIN ($)"  value={C.min_liquidity_usd}  onChange={v => sc('min_liquidity_usd', v)} />
              <CfgInput label="BUYERS 5MIN MIN"    value={C.min_buyers_5min}    onChange={v => sc('min_buyers_5min', v)}    min={1} />
              <CfgInput label="VOLUME 5MIN ($)"    value={C.min_volume_5min_usd} onChange={v => sc('min_volume_5min_usd', v)} />
              <CfgInput label="MONTANT (SOL)"      value={C.amount_sol}         onChange={v => sc('amount_sol', v)}         step={0.01} min={0.001} />
              <CfgInput label="MAX POSITIONS"      value={C.max_positions}      onChange={v => sc('max_positions', v)}      min={1} max={10} />
            </div>
            <div style={S.divider}>GESTION DU RISQUE</div>
            <div style={{ ...S.grid3, paddingTop: 8, paddingBottom: 12 }}>
              <CfgInput label="STOP-LOSS (%)"      value={C.stop_loss_pct}      onChange={v => sc('stop_loss_pct', v)}      min={1} max={100} />
              <CfgInput label="TAKE-PROFIT (%)"    value={C.take_profit_pct}    onChange={v => sc('take_profit_pct', v)}    min={1} />
              <CfgInput label="TRAILING STOP (%)"  value={C.trailing_stop_pct}  onChange={v => sc('trailing_stop_pct', v)} min={0} max={100} />
            </div>
          </>)}

          <div style={{ padding: '10px 16px', borderTop: '1px solid #1a2030' }}>
            <button
              disabled={snipeSaving}
              onClick={() => saveToSupabase(setSnipeSaving, setSnipeSaved)}
              style={{
                padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: snipeSaving ? 'not-allowed' : 'pointer', opacity: snipeSaving ? 0.6 : 1,
                background: snipeSaved ? 'rgba(0,229,204,0.15)' : 'rgba(0,229,204,0.08)',
                border: '1px solid ' + (snipeSaved ? '#00e5cc66' : 'rgba(0,229,204,0.25)'),
                color: snipeSaved ? '#00e5cc' : '#c0c8d8',
              }}
            >
              {snipeSaving ? '⏳ Sauvegarde...' : snipeSaved ? '✅ Sauvegardé !' : '💾 Sauvegarder'}
            </button>
          </div>

          <div style={{ borderTop: '1px solid #1a2030' }}>
            <div style={{ padding: '8px 16px', fontSize: 9, fontWeight: 700, color: '#3a4a5a', letterSpacing: '0.8px' }}>
              HISTORIQUE ({snipeH.length > 0 ? snipeH.length + ' trades' : 'aucun'})
            </div>
            {snipeH.length === 0
              ? <div style={{ padding: '14px 16px', textAlign: 'center', color: '#2a3040', fontSize: 11 }}>Aucun snipe réalisé</div>
              : snipeH.map(t => <HistoryRow key={t.id} t={t} />)
            }
          </div>
        </div>

        {/* ═══════════════ Section 2 : Momentum ═══════════════ */}
        <div style={S.card}>
          <div style={S.head}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#c0c8d8' }}>📈 Momentum Raydium</div>
              <div style={{ fontSize: 10, color: '#3a4a5a', marginTop: 2 }}>Helius onLogs Raydium V4 · Fenêtre glissante 10 minutes · ~400ms latence</div>
            </div>
            <Toggle value={momentumEnabled} onChange={setMomentumEnabled} />
          </div>

          <StatRow items={[
            ['POSITIONS OUVERTES', momH.filter(t => t.resultat === 'En cours').length],
            ['ENTRÉES TOTAL',      momH.length],
            ['GAGNANTS',           momWin],
            ['WIN RATE',           momWr, momClosed.length ? (momWin / momClosed.length >= 0.5 ? '#00e5cc' : '#ff6b6b') : '#5a6a80'],
          ]} />

          {momentumEnabled && (<>
            <div style={S.divider}>DÉTECTION (FENÊTRE 10 MIN)</div>
            <div style={S.grid3}>
              <CfgInput label="VOL MIN SOL (10min)"  value={M.min_volume_sol_10min} onChange={v => mc('min_volume_sol_10min', v)} min={1} />
              <CfgInput label="TRADERS UNIQUES MIN"  value={M.min_unique_traders}   onChange={v => mc('min_unique_traders', v)}   min={1} />
              <CfgInput label="HAUSSE MAX 1H (%)"    value={M.max_price_up_pct}     onChange={v => mc('max_price_up_pct', v)}     min={0} />
              <CfgInput label="LIQUIDITÉ MIN ($)"    value={M.min_liquidity_usd}    onChange={v => mc('min_liquidity_usd', v)} />
              <CfgInput label="AGE MIN (heures)"     value={M.min_age_h}            onChange={v => mc('min_age_h', v)}            min={0} />
              <CfgInput label="AGE MAX (heures)"     value={M.max_age_h}            onChange={v => mc('max_age_h', v)}            min={0} />
            </div>
            <div style={S.divider}>TRADE</div>
            <div style={{ ...S.grid3, paddingTop: 8 }}>
              <CfgInput label="MONTANT (SOL)"        value={M.amount_sol}           onChange={v => mc('amount_sol', v)}           step={0.01} min={0.001} />
              <CfgInput label="MAX POSITIONS"        value={M.max_positions}        onChange={v => mc('max_positions', v)}        min={1} max={10} />
            </div>
            <div style={S.divider}>GESTION DU RISQUE</div>
            <div style={{ ...S.grid3, paddingTop: 8, paddingBottom: 12 }}>
              <CfgInput label="STOP-LOSS (%)"        value={M.stop_loss_pct}        onChange={v => mc('stop_loss_pct', v)}        min={1} max={100} />
              <CfgInput label="TAKE-PROFIT (%)"      value={M.take_profit_pct}      onChange={v => mc('take_profit_pct', v)}      min={1} />
              <CfgInput label="TRAILING STOP (%)"    value={M.trailing_stop_pct}    onChange={v => mc('trailing_stop_pct', v)}   min={0} max={100} />
            </div>
          </>)}

          <div style={{ padding: '10px 16px', borderTop: '1px solid #1a2030' }}>
            <button
              disabled={momSaving}
              onClick={() => saveToSupabase(setMomSaving, setMomSaved)}
              style={{
                padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: momSaving ? 'not-allowed' : 'pointer', opacity: momSaving ? 0.6 : 1,
                background: momSaved ? 'rgba(0,229,204,0.15)' : 'rgba(0,229,204,0.08)',
                border: '1px solid ' + (momSaved ? '#00e5cc66' : 'rgba(0,229,204,0.25)'),
                color: momSaved ? '#00e5cc' : '#c0c8d8',
              }}
            >
              {momSaving ? '⏳ Sauvegarde...' : momSaved ? '✅ Sauvegardé !' : '💾 Sauvegarder'}
            </button>
          </div>

          <div style={{ borderTop: '1px solid #1a2030' }}>
            <div style={{ padding: '8px 16px', fontSize: 9, fontWeight: 700, color: '#3a4a5a', letterSpacing: '0.8px' }}>
              HISTORIQUE ({momH.length > 0 ? momH.length + ' trades' : 'aucun'})
            </div>
            {momH.length === 0
              ? <div style={{ padding: '14px 16px', textAlign: 'center', color: '#2a3040', fontSize: 11 }}>Aucune entrée momentum réalisée</div>
              : momH.map(t => <HistoryRow key={t.id} t={t} />)
            }
          </div>
        </div>

      </div>
    </div>
  );
}
